<template>
  <div
    class="page max-w-[1010px]"
    data-test="page"
  >
    <div
      class="header"
      data-test="header"
    >
      My Public Page
      <div class="flex flex-row gap-4">
        <Button
          size="small"
          variant="outlined"
          @click="viewPage(donorStore.domain, companyAdminStore.company.customUrl)"
          data-test="view-page-button"
        >
          <Eye
            class="w-[15px] h-[15px]"
            :fill="donorStore.mainColor"
          />
          <span>View Page</span>
        </Button>
      </div>
    </div>
    <div
      class="text-sm weight-600 title mb-3 text-center about-us-title"
      data-test="about-us-title"
    >
      ABOUT US
      <div style="width: 25px; height: 18px; padding-left: 8px; display: inline-block">
        <Pencil
          v-if="!editStates.aboutUs"
          :fill="mainColor"
          class="cursor-pointer"
          @click="editStates.aboutUs = true"
          data-test="edit-about-us"
        />
      </div>
    </div>

    <Card
      class="card-style"
      data-test="about-us-card"
    >
      <template #content>
        <div
          v-if="!companyAdminStore.isCompanyLoaded"
          class="flex-container mb-12"
          data-test="about-us-skeleton"
        >
          <div class="left-column">
            <Skeleton
              width="100%"
              height="20px"
              class="mb-2"
            />
            <Skeleton
              width="100%"
              height="20px"
              class="mb-2"
            />
            <Skeleton
              width="100%"
              height="20px"
              class="mb-2"
            />
          </div>
          <div class="right-column">
            <Skeleton
              width="100%"
              height="200px"
            />
          </div>
        </div>
        <div
          v-else
          class="flex-container mb-12"
          data-test="about-us-content"
        >
          <div
            class="flex-1"
            v-if="editStates.aboutUs"
          >
            <div class="flex-1 px-14">
              <div>
                <AboutUsForm />
                <FeaturedCausesForm :showOrganizationsDropdown="false" />
              </div>
            </div>
          </div>
          <div v-else>
            <div class="flex-container mb-6">
              <div class="left-column">
                <div>
                  <label for="aboutUs">About Us</label>
                  <div id="aboutUs">
                    {{ companyAdminStore.company.aboutUs }}
                  </div>
                </div>
                <div>
                  <label for="customUrl">Custom Url</label>
                  <div id="customUrl">
                    {{ companyAdminStore.company.customUrl }}
                  </div>
                </div>
              </div>
              <div>
                <label for="aboutUsImage">"About Us" Image</label>
                <div
                  id="aboutUsImage"
                  class="right-column about-us-image"
                  data-test="about-us-image"
                >
                  <Image :src="companyAdminStore.company.aboutUsImage" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div
          v-if="editStates.aboutUs"
          class="footer-buttons px-16"
          data-test="about-us-footer"
        >
          <Button
            :style="{ color: donorStore.mainColor }"
            :loading="isSavingStates.aboutUs"
            variant="outlined"
            @click="cancelChanges('aboutUs')"
            >Cancel</Button
          >
          <Button
            :style="`background-color: ${donorStore.mainColor}; color: ${getContrastYIQ(donorStore.mainColor)}`"
            :loading="isSavingStates.aboutUs"
            @click="saveChanges('aboutUs', [companyAdminStore.aboutUsForm, companyAdminStore.featuredCausesForm])"
            data-test="save-about-us"
            >Save Changes</Button
          >
        </div>
      </template>
    </Card>

    <div
      class="text-sm weight-600 title mb-3 text-center gives-day-info-title"
      data-test="gives-day-info-title"
    >
      GIVES DAY INFORMATION
      <div style="width: 25px; height: 18px; padding-left: 8px; display: inline-block">
        <Pencil
          v-if="!editStates.givesDayInfo"
          :fill="mainColor"
          class="cursor-pointer"
          @click="editStates.givesDayInfo = true"
          data-test="edit-gives-day-info"
        />
      </div>
    </div>

    <Card class="card-style">
      <template #content>
        <div
          v-if="!companyAdminStore.isCompanyLoaded"
          class="flex-container mb-12"
        >
          <div class="left-column">
            <Skeleton
              width="100%"
              height="20px"
              class="mb-2"
            />
            <Skeleton
              width="100%"
              height="20px"
              class="mb-2"
            />
          </div>
        </div>
        <div
          v-else
          class="flex-container mb-12"
          data-test="gives-day-content"
        >
          <div
            v-if="editStates.givesDayInfo"
            class="flex-1 px-16"
          >
            <div>
              <GivesDayForm />
            </div>
          </div>
          <div v-else>
            <div class="flex-container mb-6">
              <div class="left-column">
                <div>
                  <label for="companyGoal">Goal</label>
                  <div
                    id="companyGoal"
                    data-test="company-goal-card"
                  >
                    {{ formatMoney(companyAdminStore.company.companyGoal) }}
                  </div>
                </div>
                <div>
                  <label for="featuredCauses">Featured Causes Text</label>
                  <div id="featuredCauses">
                    {{ companyAdminStore.company.featuredCausesText }}
                  </div>
                </div>
                <div>
                  <label for="aboutUs">Gives Day Participation</label>
                  <div id="aboutUs">
                    {{ companyAdminStore.company.participationText }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div
          v-if="editStates.givesDayInfo"
          class="footer-buttons px-16"
        >
          <Button
            :style="{ color: donorStore.mainColor }"
            :loading="isSavingStates.givesDayInfo"
            variant="outlined"
            @click="cancelChanges('givesDayInfo')"
            >Cancel</Button
          >
          <Button
            :style="`background-color: ${donorStore.mainColor}; color: ${getContrastYIQ(donorStore.mainColor)}`"
            :loading="isSavingStates.givesDayInfo"
            @click="saveChanges('givesDayInfo', [companyAdminStore.givesDayForm])"
            data-test="save-gives-day-info"
            >Save Changes</Button
          >
        </div>
      </template>
    </Card>

    <div
      class="text-sm weight-600 title mb-3 text-center gives-day-info-title"
      data-test="media-title"
    >
      MEDIA
      <div style="width: 25px; height: 18px; padding-left: 8px; display: inline-block">
        <Pencil
          v-if="!editStates.media"
          :fill="mainColor"
          class="cursor-pointer"
          @click="editStates.media = true"
          data-test="edit-media"
        />
      </div>
    </div>
    <Card class="card-style">
      <template #content>
        <div v-if="!companyAdminStore.isCompanyLoaded">
          <div class="image-loader-container">
            <Skeleton
              width="100%"
              height="147px"
              class="mb-2"
            />
            <Skeleton
              width="100%"
              height="147px"
              class="mb-2"
            />
            <Skeleton
              width="100%"
              height="147px"
              class="mb-2"
            />
          </div>
        </div>
        <div
          v-else
          class="flex-container mb-12"
          data-test="media-content"
        >
          <div
            v-if="editStates.media"
            class="flex-1 px-16"
          >
            <div>
              <MediaForm />
            </div>
          </div>
          <div v-else>
            <div class="image-container">
              <div
                class="media-images"
                v-for="(image, index) in companyAdminStore.company.images"
                :key="index"
                :data-test="`media-image-${index}`"
              >
                <Image :src="image" />
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div
          v-if="editStates.media"
          class="footer-buttons px-16"
        >
          <Button
            :style="{ color: donorStore.mainColor }"
            :loading="isSavingStates.media"
            variant="outlined"
            @click="cancelChanges('media')"
            >Cancel</Button
          >
          <Button
            :style="`background-color: ${donorStore.mainColor}; color: ${getContrastYIQ(donorStore.mainColor)}`"
            :loading="isSavingStates.media"
            @click="saveChanges('media', [companyAdminStore.mediaForm])"
            data-test="save-media"
            >Save Changes</Button
          >
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup>
import { ref, onBeforeMount } from 'vue';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useCompanyAdminStore } from '../../../stores/CompanyAdminStore';
import { useToastStore } from '@/stores/ToastStore.js';
import getContrastYIQ from '@/helpers/getContrastYIQ';
import MediaForm from '../../../components/companyGiving/MediaForm.vue';
import AboutUsForm from '../../../components/companyGiving/AboutUsForm.vue';
import FeaturedCausesForm from '../../../components/companyGiving/FeaturedCausesForm.vue';
import GivesDayForm from '../../../components/companyGiving/GivesDayForm.vue';
import filters from '@/helpers/filters.js';
import Pencil from '@/assets/Pencil.vue';
import Eye from '@/assets/Eye.vue';
import viewPage from '@/helpers/viewPage';

const formatMoney = filters.formatMoney;

const donorStore = useDonorStore();
const companyAdminStore = useCompanyAdminStore();

const mainColor = donorStore.mainColor;

const editStates = ref({
  aboutUs: false,
  givesDayInfo: false,
  media: false,
});

const isSavingStates = ref({
  aboutUs: false,
  givesDayInfo: false,
  media: false,
});

const cancelChanges = (section) => {
  editStates.value[section] = false;
};

const saveChanges = async (section, forms) => {
  isSavingStates.value[section] = true;
  try {
    await companyAdminStore.saveForm([...forms, { status: 6434 }]);

    const formattedSection = section
      .replace(/([A-Z])/g, ' $1')
      .replace(/-/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .trim();
    const summary = `${formattedSection} Updated`;
    const detail = `Your ${formattedSection} has been updated successfully.`;

    useToastStore().add({
      severity: 'success',
      summary,
      detail,
      life: 3000,
    });

    editStates.value[section] = false;
  } catch (e) {
    useToastStore().add({
      severity: 'error',
      summary: 'Error',
      detail: e.message,
      life: 3000,
    });
  } finally {
    isSavingStates.value[section] = false;
  }
};

onBeforeMount(async () => {
  await companyAdminStore.getCompany();
});
</script>

<style scoped>
.card-style {
  display: flex;
  width: 100%;
  max-width: 930px;
  margin-bottom: 42px;
}
.char-count {
  color: var(--purple-400, #54698d);
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 15px; /* 125% */
}
.footer-buttons {
  display: flex;
  justify-content: space-between;
}
.styled-text {
  color: #000;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
}
label {
  font-size: 12px;
}
.about-us-title,
.gives-day-info-title {
  font-size: 20px;
}
.styled-textarea {
  width: 430px;
  height: 171px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1px solid var(--White, #fff);
  background: var(--White, #fff);
  border-color: v-bind('donorStore.mainColor');
}
.styled-input {
  width: 288px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1px solid var(--White, #fff);
  background: var(--White, #fff);
  border-color: v-bind('donorStore.mainColor');
}
.about-us-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  max-width: 329px;
  height: auto;
  border-radius: 8px;
  overflow: hidden;
}

.media-images {
  width: 254px;
  height: 147px;
  margin-bottom: 24px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.left-column {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  flex-shrink: 0;
}
.right-column {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex-shrink: 0;
}
.image-container {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding: 24px;
}
.flex-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.image-loader-container {
  display: flex;
  justify-content: space-between;
  gap: 24px;
}
@media (min-width: 768px) {
  .left-column {
    width: 430px;
  }
  .right-column {
    width: 329px;
    flex-direction: row;
  }
  .flex-container {
    flex-direction: row;
  }
}

@media (max-width: 767px) {
  .page {
    padding: 0 24px;
  }
}
</style>
